import React, { useState, useEffect, useRef } from "react";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import EditIcon from "../../../../static/images/EditIcon";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import ModalPopup from "components/common/atoms/ModalPopup";
import { ApiCall } from "components/common/services/ApiServices";
import CustomNotify from "components/common/atoms/CustomNotify";
import { CLOSE_FOLLOW_UP, GET_FOLLOW_UPS } from "routes/ApiEndpoints";
import Title from "components/common/atoms/Title";
import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import LinkTo from "components/common/atoms/LinkTo";
import * as CONST from "../../../../Constants";
import { useSelector } from "react-redux";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import type { Option, OptionProps } from "components/common/utlis/TypeAnnotations";
import {
  createQueryParams,
  filterAndMerge,
  getQueryParamsFromRedux,
} from "components/common/utlis/HelperFunctions";
import {
  clearDestinationPath,
  setDestinationPath,
} from "features/NavigationSlice";
import { useDispatch } from "react-redux";
import type { RootState } from "store";
import SearchFields from "components/common/atoms/SearchFields";
import { createFilterConfig } from "./Filters";
import ResetBtn from "components/common/atoms/ResetBtn";
import type { FollowUpData } from "../TaskAnnotations";
import { isCompletionReasonType } from "../TaskConstants";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import CloseFile from "static/images/CloseFile";
import PaginationNew from "components/common/atoms/PaginationNew";
import Sorting from "components/common/atoms/Sorting";
import ActionIconWrapper from "components/common/atoms/ActionIconWrapper";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import NoRecords from "components/common/atoms/NoRecords";
import { selectAuth } from "features/auth/AuthSlice";
import ViewIcon from "static/images/ViewIcon";
import GetQueryParams from "components/common/services/GetQueryParams";
import ExportFile from "components/common/atoms/ExportFile";

interface SearchProps {
  taskType: OptionProps[];
  status: Option | null;
  office: number[];
  company: string;
  candidate: string;
  vacancy: string;
  taskStart: Date | string | null;
  taskEnd: Date | string | null;
  followUpStart: Date | string | null;
  followUpEnd: Date | string | null;
  // createdAt: Date | string | null;
  createdBy: Option | null;
  followedBy: Option | null;
  userIds: number[];
  currentPage: number;
  recordsPerPage: number;
  orderByColumn: string;
  orderIn: "" | "asc" | "desc";
  fromDashboard: boolean;
  isRecruiter: boolean;
  source?: "candidateCorner" | "manual";
}

export const ManageFollowUps: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [deleteID, setDeleteId] = useState<
    | {
        id: string;
        hasfollowup: string | null;
        status: boolean | null;
      }
    | undefined
  >();
  const [showModal, setShowModal] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [followUps, setFollowUps] = useState<FollowUpData[]>([]);
  const [paginationData, setPaginationData] = useState({
    totalPages: 1,
    totalRecords: 1,
    cursor: null,
  });
  // Dropdown options are static; fetch them once and reuse on later searches.
  const dropdownLoadedRef = useRef(false);
  const [dropdownList, setDropdownList] = useState({
    offices: [],
    taskTypes: [],
    status: [
      { value: 0, label: "Open" },
      { value: 1, label: "Closed" },
    ],
    reasons: []
  });
  const destinationPath = useSelector(
    (state: RootState) => state.navigation.destinationPath
  );
  const filters = createFilterConfig(dropdownList);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authUser = useSelector(selectAuth);
  const getUrlParams = GetQueryParams();
  const paramFilter = getUrlParams.get("filter");
  const hasFilterParam = paramFilter === "1";
  const reduxParams = getQueryParamsFromRedux( destinationPath, paramFilter ?? undefined );
  const queryParams = hasFilterParam ? reduxParams : reduxParams?.source === "candidateCorner" ? {} : reduxParams;

  // Initialize search data based on query params
  const defaultSearchData: SearchProps = {
    taskType: [],
    office:
      authUser?.role === CONST.RECRUITER
        ? [authUser?.officeId, authUser?.correspondingOffice?.id]
        : [],
    userIds: [],
    company: "",
    candidate: "",
    vacancy: "",
    taskStart: "",
    taskEnd: "",
    followUpStart: "",
    followUpEnd: "",
    // createdAt: "",
    createdBy:
      authUser?.role === CONST.RECRUITER ||
        authUser?.role === CONST.OFFICE_MANAGER
        ? authUser?.fullName
        : null,
    followedBy: null,
    currentPage: 1,
    recordsPerPage: 10,
    orderByColumn: "",
    orderIn: "",
    fromDashboard: false,
    isRecruiter: false,
    status: { value: 0, label: "Open" },
    source: undefined,
  };

  //advanced search
  const defaultAdvSearchData = {
    street: "",
  };

  //active tab status
  const defaultTabData = {
    tabFieldName: "companyType",
    tabFieldValue: null,
  };

  const filteredSearchData = filterAndMerge(defaultSearchData, queryParams);
  const filteredAdvSearchData = filterAndMerge(
    defaultAdvSearchData,
    queryParams
  );
  const filteredTabData = filterAndMerge(defaultTabData, queryParams);

  // Initialize search data based on query params, merging with default values
  const [searchData, setSearchData] = useState<SearchProps>({
    ...defaultSearchData,
    ...filteredSearchData,
  });

  const [advSearchData, setAdvSearchData] = useState({
    ...defaultAdvSearchData,
    ...filteredAdvSearchData,
  });

  const [activeTab, setActiveTab] = useState({
    ...defaultTabData,
    ...filteredTabData,
  });

  useEffect(() => {
    const filterData = { ...searchData, ...advSearchData, ...activeTab };
    const searchUrl = createQueryParams(filterData);
    dispatch(setDestinationPath(searchUrl));
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchDetails(filterData);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchData]);

  const fetchDetails = async (filterData: any) => {
    setLoading(true);
    const data = {
      search: filterData,
      loadDropdown: !dropdownLoadedRef.current,
    };

    const statusParam = searchParams.get("status");
    const response = await ApiCall.service(
      `${GET_FOLLOW_UPS}/${statusParam}`,
      "POST",
      data,
      false,
      CONST.CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
      const { followUpsData, dropdownData, paginationData } = response?.data;
      setFollowUps(followUpsData);
      setPaginationData(paginationData);
      if (dropdownData) {
        setDropdownList(dropdownData);
        dropdownLoadedRef.current = true;
      }
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured")
      });
    }
    setLoading(false);
  };

  //** change handlers */
  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      fromDashboard: false,
      [name]: value,
    }));
  };

  const handlePageChange = (pageNumber: number) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: pageNumber,
    }));
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      fromDashboard: false,
      [fieldName]: selectedOption,
    }));
  };

  const handleAutoSelectChange = (selectedOption: any, fieldName: string) => {
    // const differenceOption = selectedOption.filter(
    //     (item1: Option) => !searchData.company.some(item2 => item1.value === item2.value)
    //   );
    // if (searchData.company.length === 0) {
    //     setSearchData((prevSearch) => ({
    //         ...prevSearch,
    //         currentPage: 1,
    //         [fieldName]: selectedOption,
    //     }));
    // } else if (differenceOption) {
    //     setSearchData((prevSearch) => ({
    //         ...prevSearch,
    //         currentPage: 1,
    //         [fieldName]: differenceOption,
    //     }));
    // }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setSearchData((prev) => ({
      ...prev,
      recordsPerPage: newItemsPerPage,
      currentPage: 1,
    }));
  };

  //** search filters */
  const handleReset = async (e: React.FormEvent) => {
    dispatch(clearDestinationPath());
    setSearchData({
      ...defaultSearchData,
      office: [],
      status: null,
      createdBy: null,
    });
    setAdvSearchData(defaultAdvSearchData);
    setActiveTab(defaultTabData);
  };

  const handleDateChange = (date: Date | null, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      fromDashboard: false,
      [fieldName]: date,
    }));
  };

  const handleModalClose = () => setShowModal(!showModal);
  // Task types that require a completion reason go straight to the reason popup;
  // the "Do you want to add feedback?" step only applies to the other types, where
  // closing needs no extra input. `taskTypeId` is passed in because `deleteID` is
  // only set here and is not readable yet via getDeleteFollowUp().
  const confirmationPopup = (
    id: string,
    hasfollowup: string | null,
    status: boolean | null,
    taskTypeId: number | null
  ) => {
    setDeleteId({ id, hasfollowup, status });
    if (isCompletionReasonType(taskTypeId)) {
      setShowReasonModal(true);
    } else {
      setShowModal(true);
    }
  };

  const getDeleteFollowUp = () =>
  followUps.find((f) => f.latest_task_id === deleteID?.id);

  const handleAddFeedback = async () => {
    setShowModal(false);
    const data = getDeleteFollowUp();
    const id = data?.id;
    navigate(`/edit/follow-up/${id}?close=1`);
  };


  const handleFollowUpClose = async () => {
    if (!deleteID?.id) return;

    const followUp = getDeleteFollowUp();
    if (followUp && isCompletionReasonType(followUp.task_type_id)) {
      setShowModal(false);
      setShowReasonModal(true);
      return;
    }

    await closeFollowUp();
  };

  const closeFollowUp = async (reason: any = null) => {
    if (!deleteID?.id) return;

    const requestData = { id: deleteID.id, reason, hasfollowup : deleteID.hasfollowup, status :deleteID.status };

    const response = await ApiCall.service(
      CLOSE_FOLLOW_UP,
      "POST",
      requestData,
      true,
      CONST.CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
      // setFollowUps((prevItems) =>
      //   prevItems.map((item) =>
      //     item.id === deleteID ? { ...item, completed: true } : item
      //   )
      // );
      // setFollowUps((prevItems) =>
      //   prevItems.map((item) =>
      //     item.latest_task_id === deleteID.id ? { ...item, status: true } : item
      //   )
      // );
      // Re-fetch with the active filters so the closed follow-up drops out of
      // the current (e.g. "Open") view instead of lingering until a refresh.
      const filterData = { ...searchData, ...advSearchData, ...activeTab };
      fetchDetails(filterData);
      CustomNotify({ type: "success", message: response?.msg });
    } else {
      CustomNotify({
        type: "error",
        message: t("Some error occured")
      });
    }
    setDeleteId(undefined);
    setShowModal(false);
    setShowReasonModal(false);
    setSelectedReason(null);
  };

  const handleReasonConfirm = async () => {
    if (!selectedReason) {
      CustomNotify({ type: "warning", message: t("Please select a reason") });
      return;
    }
    await closeFollowUp(selectedReason);
  };

  const handleReasonModalClose = () => {
    setShowReasonModal(false);
    setSelectedReason(null);
    setDeleteId(undefined);
  };

  const handleOrderByColumn = (column: string) => {
    let newOrder: "" | "asc" | "desc" = ""; // Explicit type declaration
    let newColumn = column;

    if (searchData.orderByColumn === column) {
      // Cycle through the three possible order values: '', 'asc', 'desc'
      if (searchData.orderIn === "") {
        newOrder = "asc"; // From '' to 'asc'
      } else if (searchData.orderIn === "asc") {
        newOrder = "desc"; // From 'asc' to 'desc'
      } else {
        newOrder = ""; // From 'desc' to ''
        newColumn = ""; // Reset column if no order
      }
    } else {
      // If it's a new column, start with ascending
      newOrder = "asc";
    }

    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1, // Reset pagination on sort change
      orderByColumn: newColumn, // Set the new column or empty string
      orderIn: newOrder, // Set the new order (asc, desc, or empty)
    }));
  };

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Company",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={t("Manage action")} />
      <div className="row">
        <div className="col-12">
          <div className="position-relative tableMainWrapper">
            <div className="row">
              <div className="ManageCreateBtn">
                <AccessControl
                  requiredPermissions={[
                    {
                      permission: "Follow-up",
                      create: true,
                    },
                  ]}
                >
                  <LinkTo
                    pagelink={"/add/follow-up"}
                    title={t("New action")}
                    icon={faPlus}
                  />
                </AccessControl>
                <AccessControl
                  requiredPermissions={[
                    {
                      permission: "Export Files",
                      read: true,
                    },
                  ]}
                >
                  <div style={{ marginLeft: "1vw" }}>
                    <ExportFile
                      source="follow-ups"
                      searchParams={searchData}
                      microService={CONST.CENTRAL_DATA_MANAGEMENT_MICROSERVICE}
                    />
                  </div>
                </AccessControl>
              </div>
            </div>
            <div className="table-responsive tableSection">
              <table className="table border-0 mb-0 manageFollowup">
                <thead>
                  <tr className="tableSearchFields">
                    <SearchFields
                      searchData={searchData}
                      filters={filters}
                      handleSearchChange={handleSearchChange}
                      handleSelectChange={handleSelectChange}
                      handleAutoSelectChange={handleAutoSelectChange}
                      handleDateChange={handleDateChange}
                    />
                    <th className="border-0">
                      <div className="d-flex align-items-center w-100">
                        <ResetBtn handleResetClick={handleReset} />
                      </div>
                    </th>
                  </tr>
                </thead>
              </table>
              <div className="tableContainer scrollBarDesign">
                <table className="table table-hover manageFollowup">
                  <thead>
                    <tr className="TableHeader">
                      <th onClick={(e) => handleOrderByColumn("office")}>
                        <div className="sortableHeader">
                          {t("Office")}
                          <Sorting
                            sortType={
                              searchData.orderByColumn === "office"
                                ? searchData.orderIn
                                : ""
                            }
                          />
                        </div>
                      </th>
                      <th onClick={(e) => handleOrderByColumn("taskDate")}>
                        <div className="sortableHeader">
                          {t("Time of action")}
                          <Sorting
                            sortType={
                              searchData.orderByColumn === "taskDate"
                                ? searchData.orderIn
                                : ""
                            }
                          />
                        </div>
                      </th>
                      {/* <th onClick={(e) => handleOrderByColumn("createdAt")}>
                        {t("Created At")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "createdAt"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th> */}
                      <th onClick={(e) => handleOrderByColumn("createdBy")}>
                        <div className="sortableHeader">
                          {t("Created by")}
                          <Sorting
                            sortType={
                              searchData.orderByColumn === "createdBy"
                                ? searchData.orderIn
                                : ""
                            }
                          />
                        </div>
                      </th>
                      <th
                        onClick={(e) => handleOrderByColumn("task_type.name")}
                      >
                        <div className="sortableHeader">
                          {t("Type of action")}
                          <Sorting
                            sortType={
                              searchData.orderByColumn === "task_type.name"
                                ? searchData.orderIn
                                : ""
                            }
                          />
                        </div>
                      </th>
                      <th>{t("Status")}</th>
                      <th>{t("Company")}</th>
                      <th>{t("Candidate")}</th>
                      <th>{t("Vacancy / Function")}</th>
                      <th>{t("Info")}</th>
                      <th onClick={(e) => handleOrderByColumn("followDate")}>
                        <div className="sortableHeader">
                          {t("Next action")}
                          <Sorting
                            sortType={
                              searchData.orderByColumn === "followDate"
                                ? searchData.orderIn
                                : ""
                            }
                          />
                        </div>
                      </th>
                      <th onClick={(e) => handleOrderByColumn("followedBy")}>
                        <div className="sortableHeader">
                          {t("To be followed by")}
                          <Sorting
                            sortType={
                              searchData.orderByColumn === "followedBy"
                                ? searchData.orderIn
                                : ""
                            }
                          />
                        </div>
                      </th>
                      <AccessControl
                        requiredPermissions={[
                          {
                            permission: "Follow-up",
                            update: true,
                          },
                        ]}
                        actions={true}
                      >
                        <th>{t("Action")}</th>
                      </AccessControl>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <SpinnerWrapper headerLength={12} />
                    ) : (
                      <>
                        {followUps && followUps.length > 0 ? (
                          followUps.map((followUp, index) => (
                            <tr className="align-middle" key={index}>
                              <td className="text-break">{followUp.office}</td>
                              <td className="text-break">
                                {followUp.time}
                              </td>
                              {/* <td>
                                <TextEllipsis
                                  label={followUp.createdat}
                                  title={followUp.createdat}
                                  width="5.5vw"
                                />
                              </td> */}

                              <td className="text-break">
                                {followUp.createdby}
                              </td>
                              <td className="text-break">
                                {t(`${followUp.taskType}`)}
                              </td>
                              <td data-label={t("Status")}>
                                <span
                                  className={`d-inline-block rounded-circle status-icon ${!followUp.status && followUp.status !== null
                                      ? "bg-warning"
                                      : "bg-success"
                                    }`}
                                  data-toggle="tooltip"
                                  style={{ marginLeft: "0.8vw" }}
                                  title={t(
                                    `${CONST.FOLLOW_UP_STATUS[
                                    !followUp.status &&
                                      followUp.status !== null
                                      ? 0
                                      : 1
                                    ]
                                    }`
                                  )}
                                />
                              </td>
                              <td className="text-break">
                                <Link
                                  to={`/company/${followUp.company?.id}`}
                                  target="_blank"
                                >
                                  {followUp.company?.name ?? ""}
                                </Link>
                              </td>
                              <td className="text-break">
                                <Link
                                  to={`/candidate/edit/${followUp.candidate?.id}`}
                                  target="_blank"
                                >
                                  {followUp.candidate?.name ?? ""}
                                </Link>
                              </td>
                              <td className="text-break">
                                <Link
                                  to={`/vacancy/${followUp.vacancy?.id}`}
                                  target="_blank"
                                >
                                  {followUp.vacancy?.name ?? ""}
                                </Link>
                              </td>
                              <td className="text-break">
                                {t(`${followUp.info}`)}
                              </td>
                              <td className="text-break">
                                {followUp.nextfollowup}
                              </td>
                              <td className="text-break">
                                {followUp.followedby}
                              </td>
                              <AccessControl
                                requiredPermissions={[
                                  {
                                    permission: "Follow-up",
                                    update: true,
                                  },
                                ]}
                              >
                                <td className="table-action-icons">
                                  {/* Collapsed to a single chevron so the Info column gets the space */}
                                  <ActionIconWrapper initialVisibleCount={0}>
                                    <Link
                                      to={`/edit/follow-up/${followUp.id}?destination=/manage/follow-up`}
                                      title={t("Edit")}
                                      className="cursor-pointer"
                                    >
                                      <EditIcon />
                                    </Link>
                                    <Link
                                      to={`/view/follow-up/${followUp.id}`}
                                      className="border-0"
                                      title={t("View")}
                                    >
                                      <ViewIcon />
                                    </Link>
                                    {!followUp.status && (
                                      <span
                                        className="border-0 cursor-pointer"
                                        title={t("Close")}
                                        onClick={(e) =>
                                          confirmationPopup(followUp.latest_task_id, followUp.hasChild, followUp.status, followUp.task_type_id)
                                        }
                                      >
                                        <CloseFile />
                                      </span>
                                    )}
                                  </ActionIconWrapper>
                                </td>
                              </AccessControl>
                            </tr>
                          ))
                        ) : (
                          <NoRecords
                            headerLength={11}
                            message={t("No follow-ups found")}
                          />
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              {!loading && (
                <div className="pagination d-block">
                  <PaginationNew
                    currentPage={searchData.currentPage}
                    totalPages={paginationData.totalPages}
                    totalRecords={paginationData.totalRecords}
                    recordsPerPage={searchData.recordsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    onPageChange={(newPage: any) => handlePageChange(newPage)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="col-md-6 align-self-center" style={{ padding: "1vw 0" }}>
        <Link 
          to={
            location.state?.origin === "candidateCorner"
              ? `/candidate/corner/${location.state?.candidateId}`
              : "/dashboard"
          }
        // to="/dashboard" 
        className=" back-btn text-decoration-underline">
          {t("Back")}
        </Link>
      </div>
      <ModalPopup
        show={showModal}
        onHide={handleModalClose}
        title={t("Feedback")}
        body={t("Do you want to add feedback") + "?"}
        onCloseButtonClick={handleFollowUpClose}
        onConfirmButtonClick={handleAddFeedback}
        className="modal-lg"
        confirmTitle={t("Yes")}
        closeTitle={t("No")}
      />
      <ModalPopup
        show={showReasonModal}
        onHide={handleReasonModalClose}
        title={t("Completion Reason")}
        body={
          <SelectWithSearch
            title={t("Completion Reason")}
            isMandatory={true}
            search={true}
            options={
              dropdownList.reasons?.filter(
                (reason: any) =>
                  reason?.task_type_id === getDeleteFollowUp()?.task_type_id
              ) ?? []
            }
            onChange={(e: any) => setSelectedReason(e)}
            isMulti={false}
            name="reason"
            value={selectedReason}
            isTranslate={true}
          />
        }
        onConfirmButtonClick={handleReasonConfirm}
        onCloseButtonClick={handleReasonModalClose}
        className="modal-lg"
        confirmTitle={t("Confirm")}
        closeTitle={t("Cancel")}
      />
    </AccessControl>
  );
};
export default translate(ManageFollowUps);
